import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { icon } from '../shared/components/icons';
import { emptyState, callout } from '../shared/components/Narrative';
import { infoIcon } from '../shared/components/InfoIcon';
import { api } from '../shared/services/api';

// ── Types ──────────────────────────────────────────────────────────────────
// These mirror the shapes the backend agent's /api/events* endpoints return.
// Everything is optional/defensive — the page degrades gracefully if a field
// is missing (e.g. when TICKETMASTER_API_KEY is unset and the list is empty).

type Magnitude = 'small' | 'medium' | 'large' | 'major';

interface EventRecord {
  id: string;
  name: string;
  date: string;            // YYYY-MM-DD (the stay date demand lands on)
  venue?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  magnitude?: Magnitude | string | null;
  source?: string | null;  // 'manual' | 'ticketmaster' | ...
}

interface NearbyUnit {
  listing_id: string | number;   // Hostaway PMS id
  listing_name: string;
  distance_mi?: number | null;
  current_price?: number | null;
  suggested_price?: number | null;
  uplift_pct?: number | null;    // e.g. 0.18 for +18% (fraction) — handled defensively
  date?: string | null;          // the specific night this rec lands on (multi-night events)
}

interface PricingEvent extends EventRecord {
  units?: NearbyUnit[];
}

interface PricingResponse {
  events?: PricingEvent[];
  ticketmaster_enabled?: boolean;
}

// Known San Diego venues — selecting one pre-fills lat/lng on the form.
const KNOWN_VENUES: { name: string; lat: number; lng: number }[] = [
  { name: 'Petco Park', lat: 32.7073, lng: -117.1566 },
  { name: 'Snapdragon Stadium', lat: 32.7836, lng: -117.1198 },
  { name: 'San Diego Convention Center', lat: 32.7058, lng: -117.1611 },
  { name: 'Viejas Arena', lat: 32.7752, lng: -117.0719 },
  { name: 'Pechanga Arena', lat: 32.7518, lng: -117.2120 },
];

const MAGNITUDE_META: Record<Magnitude, { label: string; pill: 'pill-gray' | 'pill-blue' | 'pill-amber' | 'pill-red' }> = {
  small: { label: 'Small', pill: 'pill-gray' },
  medium: { label: 'Medium', pill: 'pill-blue' },
  large: { label: 'Large', pill: 'pill-amber' },
  major: { label: 'Major', pill: 'pill-red' },
};

// Render an event-magnitude pill, or an "Unscored" gray pill when unknown.
function magPill(mag: Magnitude | null): string {
  const meta = mag ? MAGNITUDE_META[mag] : null;
  return meta
    ? `<span class="pill ${meta.pill}">${meta.label} event</span>`
    : `<span class="pill pill-gray">Unscored</span>`;
}

// Calendar chip colours, keyed by magnitude. major=red, large=orange,
// medium=amber, small=slate; unscored falls back to indigo.
const MAGNITUDE_CHIP: Record<Magnitude, { bg: string; bd: string; fg: string }> = {
  major:  { bg: '#fef2f2', bd: '#fca5a5', fg: '#b91c1c' },
  large:  { bg: '#fff7ed', bd: '#fdba74', fg: '#c2410c' },
  medium: { bg: '#fffbeb', bd: '#fcd34d', fg: '#b45309' },
  small:  { bg: '#f8fafc', bd: '#cbd5e1', fg: '#475569' },
};
const CHIP_FALLBACK = { bg: '#eaf2ff', bd: '#bfd7fe', fg: '#1d4ed8' };

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export class EventPricingPage {
  private container: HTMLElement;
  private events: PricingEvent[] = [];
  private ticketmasterEnabled = false;
  private staged = new Set<string>(); // `${listing_id}|${date}` rows already accepted
  private filterMag = 'all';          // magnitude filter (all|major|large|medium|small)
  private viewYear = 0;               // currently-viewed calendar year
  private viewMonth = 0;              // currently-viewed calendar month (0-based)
  private formOpen = false;           // "+ Add event" collapsible state
  private modalDate: string | null = null; // date currently shown in the unit modal

  constructor(container: HTMLElement) {
    this.container = container;

    // Default to the global month if one is set, else the current month.
    const g = (window as any).globalDateRange?.month;
    const now = new Date();
    if (typeof g === 'string' && /^\d{4}-\d{2}/.test(g)) {
      this.viewYear = +g.slice(0, 4);
      this.viewMonth = +g.slice(5, 7) - 1;
    } else {
      this.viewYear = now.getFullYear();
      this.viewMonth = now.getMonth();
    }
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <div class="dash-head">
          <h1>Event Pricing</h1>
          <p>Local events drive demand — click an event night to see nearby units and raise the rate.</p>
        </div>

        <style>
          /* ── Add-event form ─────────────────────────────────────────── */
          .evp-form-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(170px, 1fr)); gap:14px; }
          .evp-field label { display:block; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:5px; }
          .evp-field input, .evp-field select { width:100%; box-sizing:border-box; padding:9px 11px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit; background:#fff; color:var(--text); transition:border-color .12s, box-shadow .12s; }
          .evp-field input:focus, .evp-field select:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
          .evp-card { border:1px solid var(--border); border-radius:12px; margin-bottom:20px; overflow:hidden; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.04); }
          .evp-card-head { padding:14px 18px; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; transition:background .12s; }
          .evp-card-head:hover { background:#fafafe; }
          .evp-form-caret { display:inline-flex; width:18px; color:var(--text-muted); transition:transform .15s; font-size:11px; }

          /* ── Tables ─────────────────────────────────────────────────── */
          .evp-table { width:100%; border-collapse:collapse; }
          .evp-table th { padding:9px 14px; text-align:left; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; border-bottom:1px solid var(--border); background:#f8fafc; }
          .evp-table td { padding:11px 14px; font-size:13px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
          .evp-table tbody tr:last-child td { border-bottom:none; }
          .evp-uplift { display:inline-block; padding:2px 8px; border-radius:12px; font-size:11px; font-weight:700; background:#dcfce7; color:#15803d; border:1px solid #86efac; margin-left:6px; }

          /* ── Buttons ────────────────────────────────────────────────── */
          .evp-accept-btn { padding:7px 14px; border-radius:8px; border:1px solid #3b82f6; background:#3b82f6; color:#fff; font-size:12px; font-weight:600; cursor:pointer; transition:background .12s, border-color .12s; white-space:nowrap; }
          .evp-accept-btn:hover:not(:disabled) { background:#2563eb; border-color:#2563eb; }
          .evp-accept-btn:disabled { background:#f0fdf4; border-color:#bbf7d0; color:#15803d; cursor:default; }
          .evp-del-btn { padding:5px 11px; border-radius:8px; border:1px solid var(--border); background:#fff; color:#dc2626; font-size:12px; font-weight:600; cursor:pointer; transition:background .12s, border-color .12s; }
          .evp-del-btn:hover { background:#fef2f2; border-color:#fecaca; }

          /* ── Calendar toolbar + legend ──────────────────────────────── */
          .evp-cal-toolbar { display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; margin:0 0 16px; }
          .evp-cal-nav { display:flex; align-items:center; gap:8px; }
          .evp-cal-nav button { width:34px; height:34px; border-radius:8px; border:1px solid var(--border); background:#fff; color:var(--text); font-size:13px; cursor:pointer; line-height:1; display:inline-flex; align-items:center; justify-content:center; transition:border-color .12s, color .12s, background .12s; }
          .evp-cal-nav button:hover { border-color:#3b82f6; color:#3b82f6; background:#f5f3ff; }
          .evp-cal-today { width:auto !important; padding:0 14px !important; font-size:12px; font-weight:600; }
          .evp-cal-month { font-size:17px; font-weight:700; color:var(--text); min-width:172px; text-align:center; letter-spacing:-0.2px; }
          .evp-legend { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
          .evp-legend-item { display:inline-flex; align-items:center; gap:6px; padding:4px 11px; border-radius:999px; font-size:12px; font-weight:600; border:1px solid var(--border); background:#fff; color:var(--text-muted); cursor:pointer; transition:border-color .12s, color .12s, background .12s; }
          .evp-legend-item:hover { border-color:#bfd7fe; color:#1d4ed8; }
          .evp-legend-item.active { border-color:#3b82f6; color:#1d4ed8; background:#eaf2ff; }
          .evp-legend-dot { width:10px; height:10px; border-radius:3px; border:1px solid; }

          /* ── Calendar grid ──────────────────────────────────────────── */
          .evp-cal-grid { display:grid; grid-template-columns:repeat(7, minmax(0, 1fr)); width:100%; border:1px solid var(--border); border-radius:12px; overflow:hidden; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.04); }
          .evp-cal-dow { padding:10px 8px; text-align:center; font-size:11px; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; background:#f8fafc; border-bottom:1px solid var(--border); border-right:1px solid var(--border); }
          .evp-cal-dow:nth-child(7n) { border-right:none; }
          .evp-cal-cell { min-height:78px; min-width:0; overflow:hidden; border-right:1px solid #eef1f5; border-bottom:1px solid #eef1f5; padding:5px 6px 6px; display:flex; flex-direction:column; gap:3px; position:relative; transition:background .12s; }
          .evp-cal-cell:nth-child(7n) { border-right:none; }
          .evp-cal-row-last { border-bottom:none; }
          .evp-cal-cell.evp-weekend { background:#fcfcfd; }
          .evp-cal-cell.evp-out { background:#fafbfc; }
          .evp-cal-cell.evp-has-events { cursor:pointer; }
          .evp-cal-cell.evp-has-events:hover { background:#f5f3ff; box-shadow:inset 0 0 0 1.5px #bfd7fe; }
          .evp-cal-daynum { font-size:12.5px; font-weight:600; color:var(--text); width:24px; height:24px; display:inline-flex; align-items:center; justify-content:center; }
          .evp-cal-cell.evp-weekend .evp-cal-daynum { color:var(--text-muted); }
          .evp-cal-cell.evp-today .evp-cal-daynum { color:#fff; background:#3b82f6; border-radius:50%; font-weight:700; box-shadow:0 1px 3px rgba(37,99,235,.4); }
          .evp-cal-cell.evp-out .evp-cal-daynum { color:#cbd5e1; }

          /* ── Event chips ────────────────────────────────────────────── */
          .evp-chip { display:flex; align-items:center; gap:5px; padding:3px 7px; border-radius:6px; font-size:11px; font-weight:600; line-height:1.25; border:1px solid; white-space:nowrap; overflow:hidden; }
          .evp-chip .evp-chip-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1; min-width:0; }
          .evp-chip .evp-chip-n { flex-shrink:0; font-size:10px; font-weight:700; padding:0 4px; border-radius:5px; background:rgba(255,255,255,.65); }
          .evp-more { font-size:10.5px; font-weight:600; color:#3b82f6; padding:1px 4px 0; }

          /* ── Modal ──────────────────────────────────────────────────── */
          #evp-modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,.5); z-index:9400; display:flex; align-items:center; justify-content:center; padding:24px 16px; overflow-y:auto; }
          #evp-modal-card { background:#fff; border-radius:16px; box-shadow:0 24px 64px rgba(0,0,0,.28); width:100%; max-width:740px; max-height:calc(100vh - 48px); display:flex; flex-direction:column; overflow:hidden; animation:evpPop .14s ease; }
          @keyframes evpPop { from { transform:translateY(8px); opacity:.4; } to { transform:none; opacity:1; } }
          #evp-modal-card .evp-m-head { display:flex; justify-content:space-between; align-items:flex-start; gap:14px; padding:18px 20px; border-bottom:1px solid var(--border); flex-shrink:0; }
          #evp-modal-card .evp-m-title { font-size:18px; font-weight:700; color:var(--text); line-height:1.25; letter-spacing:-0.2px; }
          #evp-modal-card .evp-m-sub { font-size:12.5px; color:var(--text-muted); margin-top:4px; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
          #evp-modal-card .evp-m-actions { display:flex; align-items:center; gap:10px; flex-shrink:0; }
          #evp-modal-card .evp-m-x { width:30px; height:30px; border:1px solid var(--border); border-radius:8px; background:#fff; font-size:15px; color:#94a3b8; cursor:pointer; line-height:1; display:inline-flex; align-items:center; justify-content:center; transition:background .12s, color .12s, border-color .12s; }
          #evp-modal-card .evp-m-x:hover { background:#f8fafc; color:#475569; border-color:#cbd5e1; }
          #evp-modal-card .evp-m-body { padding:18px 20px; overflow-y:auto; }
          .evp-m-event { border:1px solid var(--border); border-radius:12px; overflow:hidden; margin-bottom:16px; }
          .evp-m-event:last-child { margin-bottom:0; }
          .evp-m-event-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:13px 15px; background:#fafbfc; border-bottom:1px solid var(--border); flex-wrap:wrap; }

          /* ── Responsive ─────────────────────────────────────────────── */
          @media (max-width: 640px) {
            .evp-cal-cell { min-height:84px; padding:5px 4px 6px; }
            .evp-cal-dow { padding:8px 2px; font-size:10px; }
            .evp-chip { padding:2px 5px; font-size:10px; }
            .evp-chip .evp-chip-n { display:none; }
            .evp-cal-month { font-size:15px; min-width:120px; }
            .evp-cal-toolbar { gap:10px; }
            #evp-modal-card .evp-m-body { padding:14px 14px; }
            .evp-table th, .evp-table td { padding:8px 9px; font-size:12px; }
          }
        </style>

        <!-- Top KPIs (populated per viewed month in renderCalendar) -->
        <div id="evp-kpis"></div>

        <!-- Add event (collapsible) -->
        <div class="evp-card">
          <div class="evp-card-head" id="evp-form-toggle" style="cursor:pointer;">
            <div style="display:flex; align-items:flex-start; gap:8px;">
              <span class="evp-form-caret" id="evp-form-caret">▶</span>
              <div>
                <div style="font-size:13px; font-weight:700; color:var(--text);">Add an event</div>
                <div style="font-size:11px; color:var(--text-muted); margin-top:2px;">Pick a known San Diego venue to pre-fill its location, or enter lat/lng manually.</div>
              </div>
            </div>
          </div>
          <div id="evp-form-wrap" style="display:none; padding:18px; border-top:1px solid var(--border);">
            <form id="evp-form">
              <div class="evp-form-grid">
                <div class="evp-field" style="grid-column:span 2;">
                  <label for="evp-name">Event name</label>
                  <input id="evp-name" type="text" placeholder="e.g. Padres vs Dodgers" required />
                </div>
                <div class="evp-field">
                  <label for="evp-date">Date</label>
                  <input id="evp-date" type="date" required />
                </div>
                <div class="evp-field">
                  <label for="evp-end-date">End date <span style="color:var(--text-muted); font-weight:400;">(optional — multi-night)</span></label>
                  <input id="evp-end-date" type="date" />
                </div>
                <div class="evp-field">
                  <label for="evp-magnitude">Magnitude ${infoIcon('event_magnitude')}</label>
                  <select id="evp-magnitude">
                    <option value="small">Small</option>
                    <option value="medium" selected>Medium</option>
                    <option value="large">Large</option>
                    <option value="major">Major</option>
                  </select>
                </div>
                <div class="evp-field" style="grid-column:span 2;">
                  <label for="evp-venue-select">Known venue</label>
                  <select id="evp-venue-select">
                    <option value="">— Manual / other venue —</option>
                    ${KNOWN_VENUES.map((v, i) => `<option value="${i}">${Formatters.escapeHtml(v.name)}</option>`).join('')}
                  </select>
                </div>
                <div class="evp-field">
                  <label for="evp-venue">Venue name</label>
                  <input id="evp-venue" type="text" placeholder="Venue" />
                </div>
                <div class="evp-field">
                  <label for="evp-lat">Latitude</label>
                  <input id="evp-lat" type="number" step="any" placeholder="32.7073" />
                </div>
                <div class="evp-field">
                  <label for="evp-lng">Longitude</label>
                  <input id="evp-lng" type="number" step="any" placeholder="-117.1566" />
                </div>
              </div>
              <div id="evp-form-msg" style="font-size:12px; color:#dc2626; margin-top:10px; min-height:16px;"></div>
              <div style="margin-top:8px;">
                <button type="submit" class="evp-accept-btn" id="evp-add-btn">+ Add event</button>
              </div>
            </form>
          </div>
        </div>

        <!-- Calendar -->
        <div id="evp-recs">${LoadingSpinner.render()}</div>
      </div>
    `;

    this.bindForm();
    await this.loadData();
  }

  // ── Data ─────────────────────────────────────────────────────────────────

  private async loadData(): Promise<void> {
    const recs = document.getElementById('evp-recs');
    if (recs) recs.innerHTML = LoadingSpinner.render();

    const data = await api.get<PricingResponse>('/events/pricing');
    if (!data) {
      if (recs) recs.innerHTML = emptyState(
        'Could not load events',
        'There was a problem loading event pricing recommendations. Try refreshing the page.',
        '⚠️',
      );
      return;
    }

    // Map the API shape ({event:{...}, recommendations:[...]}) to the flat shape
    // this page renders (name/magnitude/venue/date/units + per-unit fields).
    this.events = (Array.isArray(data.events) ? data.events : []).map((e: any) => {
      const ev = e.event || e;
      return {
        id: ev.id,
        name: ev.name,
        date: ev.date,
        venue: ev.venue,
        magnitude: ev.magnitude,
        source: ev.source,
        units: (e.recommendations || e.units || []).map((r: any) => ({
          listing_id: r.listing_id,
          listing_name: r.name || r.listing_name,
          distance_mi: (r.distance_miles != null ? r.distance_miles : r.distance_mi),
          current_price: r.current_price,
          suggested_price: r.suggested_price,
          uplift_pct: r.uplift_pct,
          date: r.date || ev.date,
        })),
      };
    }) as any;
    this.ticketmasterEnabled = !!data.ticketmaster_enabled;
    this.renderCalendar();
  }

  // ── Calendar view ──────────────────────────────────────────────────────────

  // All distinct YYYY-MM-DD nights an event covers (event.date plus any
  // distinct per-unit dates — this naturally spans multi-night events).
  private eventNights(ev: PricingEvent): string[] {
    const set = new Set<string>();
    if (ev.date) set.add(ev.date);
    (ev.units || []).forEach(u => { if (u.date) set.add(u.date); });
    return [...set];
  }

  // Nearby-unit count for an event on a specific night.
  private unitsOnNight(ev: PricingEvent, date: string): NearbyUnit[] {
    const units = Array.isArray(ev.units) ? ev.units : [];
    // If units carry per-night dates, scope to that night; otherwise the
    // whole list applies (single-night event).
    const dated = units.filter(u => u.date);
    if (!dated.length) return units;
    return units.filter(u => u.date === date);
  }

  private passesFilter(ev: PricingEvent): boolean {
    if (this.filterMag === 'all') return true;
    return this.normMagnitude(ev.magnitude) === this.filterMag;
  }

  // Map of YYYY-MM-DD -> events occurring that night (filtered).
  private buildDayMap(): Map<string, PricingEvent[]> {
    const map = new Map<string, PricingEvent[]>();
    for (const ev of this.events) {
      if (!this.passesFilter(ev)) continue;
      for (const night of this.eventNights(ev)) {
        const arr = map.get(night) || [];
        arr.push(ev);
        map.set(night, arr);
      }
    }
    return map;
  }

  private renderCalendar(): void {
    const el = document.getElementById('evp-recs');
    if (!el) return;

    const sourceNote = this.ticketmasterEnabled
      ? callout('Ticketmaster auto-pull is enabled — nearby events appear automatically alongside any you add manually.', 'tip')
      : '';

    const dayMap = this.buildDayMap();

    const monthLabel = `${MONTH_NAMES[this.viewMonth]} ${this.viewYear}`;
    const monthHasEvents = [...dayMap.keys()].some(d => d.startsWith(this.monthPrefix()));

    const legend = `
      <div class="evp-legend">
        ${(['all', 'major', 'large', 'medium', 'small'] as const).map(m => {
          const active = this.filterMag === m;
          const dot = m === 'all'
            ? ''
            : `<span class="evp-legend-dot" style="background:${MAGNITUDE_CHIP[m].bg};border-color:${MAGNITUDE_CHIP[m].bd};"></span>`;
          const label = m === 'all' ? 'All' : m[0].toUpperCase() + m.slice(1);
          return `<span class="evp-legend-item ${active ? 'active' : ''}" data-mag-filter="${m}">${dot}${label}</span>`;
        }).join('')}
      </div>`;

    const toolbar = `
      <div class="evp-cal-toolbar">
        <div class="evp-cal-nav">
          <button id="evp-cal-prev" aria-label="Previous month">‹</button>
          <span class="evp-cal-month">${monthLabel}</span>
          <button id="evp-cal-next" aria-label="Next month">›</button>
          <button id="evp-cal-today" class="evp-cal-today">Today</button>
        </div>
        ${legend}
      </div>`;

    const grid = this.renderGrid(dayMap);

    const empty = monthHasEvents ? '' : emptyState(
      `No events in ${monthLabel}`,
      this.ticketmasterEnabled
        ? 'No events land near your units this month. Use “+ Add event” above to add one, or check another month.'
        : 'Use “+ Add event” above to add one, or set TICKETMASTER_API_KEY to auto-pull nearby events.',
      '🎫',
    );

    el.innerHTML = sourceNote + toolbar + grid + (monthHasEvents ? '' : `<div style="margin-top:12px;">${empty}</div>`);
    this.bindCalendarControls();
    this.renderKpis(dayMap, monthLabel);
  }

  // Top KPI row — month-scoped summary of the (filtered) calendar.
  // Presentational aggregation only; reads the same dayMap the grid renders.
  private renderKpis(dayMap: Map<string, PricingEvent[]>, monthLabel: string): void {
    const host = document.getElementById('evp-kpis');
    if (!host) return;

    const prefix = this.monthPrefix();
    const monthEvents = new Set<string>();   // distinct event ids this month
    let eventNights = 0;                      // event-nights landing this month
    let unitRecs = 0;                         // nearby-unit recs this month
    let bigDemand = 0;                        // distinct major/large events this month

    for (const [night, evs] of dayMap) {
      if (!night.startsWith(prefix)) continue;
      eventNights++;
      for (const ev of evs) {
        if (ev.id) monthEvents.add(ev.id);
        unitRecs += this.unitsOnNight(ev, night).length;
        const mag = this.normMagnitude(ev.magnitude);
        if (mag === 'major' || mag === 'large') bigDemand++;
      }
    }

    const card = (
      tone: '' | 'tone-good' | 'tone-bad',
      label: string,
      ico: string,
      value: string | number,
      cap: string,
    ) => `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;

    host.innerHTML = `
      <div class="kpi-grid cols-3">
        ${card('', 'Events this month', 'calendar', monthEvents.size,
          `${eventNights} event-night${eventNights === 1 ? '' : 's'} in ${Formatters.escapeHtml(monthLabel)}`)}
        ${card(unitRecs ? 'tone-good' : '', 'Nearby unit recs', 'building', unitRecs,
          unitRecs ? 'Units close enough to bump' : 'No nearby units in range')}
        ${card(bigDemand ? 'tone-bad' : '', 'High-demand events', 'trending', bigDemand,
          'Major or large magnitude')}
      </div>`;
  }

  private monthPrefix(): string {
    return `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}`;
  }

  private renderGrid(dayMap: Map<string, PricingEvent[]>): string {
    const first = new Date(this.viewYear, this.viewMonth, 1);
    const startDow = first.getDay(); // 0 = Sun
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const totalCells = Math.ceil((startDow + daysInMonth) / 7) * 7;

    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const headerRow = DAY_HEADERS.map(d => `<div class="evp-cal-dow">${d}</div>`).join('');

    const lastRowStart = totalCells - 7;
    let cells = '';
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - startDow + 1;
      const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
      const dow = i % 7;
      const isWeekend = dow === 0 || dow === 6;
      const lastRow = i >= lastRowStart ? ' evp-cal-row-last' : '';

      if (!inMonth) {
        cells += `<div class="evp-cal-cell evp-out${isWeekend ? ' evp-weekend' : ''}${lastRow}"></div>`;
        continue;
      }

      const dateKey = `${this.monthPrefix()}-${String(dayNum).padStart(2, '0')}`;
      const evs = dayMap.get(dateKey) || [];
      const isToday = dateKey === todayKey;

      cells += `
        <div class="evp-cal-cell ${evs.length ? 'evp-has-events' : ''}${isToday ? ' evp-today' : ''}${isWeekend ? ' evp-weekend' : ''}${lastRow}"
             ${evs.length ? `data-day="${dateKey}" role="button" tabindex="0" aria-label="View ${evs.length} event${evs.length === 1 ? '' : 's'} on day ${dayNum}"` : ''}>
          <span class="evp-cal-daynum">${dayNum}</span>
          ${this.renderDayChips(dateKey, evs)}
        </div>`;
    }

    return `<div class="evp-cal-grid">${headerRow}${cells}</div>`;
  }

  private renderDayChips(date: string, evs: PricingEvent[]): string {
    if (!evs.length) return '';
    const MAX = 2;
    const shown = evs.slice(0, MAX);
    const chips = shown.map(ev => {
      const mag = this.normMagnitude(ev.magnitude);
      const c = mag ? MAGNITUDE_CHIP[mag] : CHIP_FALLBACK;
      const n = this.unitsOnNight(ev, date).length;
      const nTag = n ? `<span class="evp-chip-n" title="${n} nearby unit${n === 1 ? '' : 's'}">${n}</span>` : '';
      return `<div class="evp-chip" style="background:${c.bg};border-color:${c.bd};color:${c.fg};" title="${Formatters.escapeHtml(ev.name)}">
        <span class="evp-chip-name">${Formatters.escapeHtml(ev.name)}</span>${nTag}
      </div>`;
    }).join('');
    const more = evs.length > MAX ? `<span class="evp-more">+${evs.length - MAX} more</span>` : '';
    return chips + more;
  }

  private bindCalendarControls(): void {
    const prev = document.getElementById('evp-cal-prev');
    if (prev) prev.onclick = () => this.shiftMonth(-1);
    const next = document.getElementById('evp-cal-next');
    if (next) next.onclick = () => this.shiftMonth(1);
    const today = document.getElementById('evp-cal-today');
    if (today) today.onclick = () => {
      const n = new Date();
      this.viewYear = n.getFullYear();
      this.viewMonth = n.getMonth();
      this.renderCalendar();
    };

    document.querySelectorAll<HTMLElement>('[data-mag-filter]').forEach(item => {
      item.onclick = () => { this.filterMag = item.dataset.magFilter || 'all'; this.renderCalendar(); };
    });

    document.querySelectorAll<HTMLElement>('[data-day]').forEach(cell => {
      cell.onclick = () => this.openDayModal(cell.dataset.day || '');
      cell.onkeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openDayModal(cell.dataset.day || ''); }
      };
    });
  }

  private shiftMonth(delta: number): void {
    let m = this.viewMonth + delta;
    let y = this.viewYear;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    this.viewMonth = m;
    this.viewYear = y;
    this.renderCalendar();
  }

  // ── Modal (units for a clicked day) ──────────────────────────────────────────

  private openDayModal(date: string): void {
    if (!date) return;
    const dayMap = this.buildDayMap();
    const evs = dayMap.get(date) || [];
    if (!evs.length) return;

    this.modalDate = date;

    // Build overlay once.
    let overlay = document.getElementById('evp-modal-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'evp-modal-overlay';
      overlay.innerHTML = `<div id="evp-modal-card"></div>`;
      document.body.appendChild(overlay);
      overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeModal(); });
    }
    overlay.style.display = 'flex';

    const card = document.getElementById('evp-modal-card');
    if (card) card.innerHTML = this.renderModalContent(date, evs);

    if (!this._escBound) {
      this._escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') this.closeModal(); };
      document.addEventListener('keydown', this._escHandler);
      this._escBound = true;
    }

    this.bindModalEvents();
  }

  private _escBound = false;
  private _escHandler: ((e: KeyboardEvent) => void) | null = null;

  private closeModal(): void {
    const overlay = document.getElementById('evp-modal-overlay');
    if (overlay) overlay.style.display = 'none';
    this.modalDate = null;
  }

  private renderModalContent(date: string, evs: PricingEvent[]): string {
    const sections = evs.map(ev => this.renderModalEvent(ev, date)).join('');

    // Count actionable rows across all events on this day for the header "Accept all".
    let actionable = 0;
    for (const ev of evs) {
      for (const u of this.unitsOnNight(ev, date)) {
        if (this.isActionable(ev, u, date)) actionable++;
      }
    }

    const acceptAll = actionable
      ? `<button class="evp-accept-btn" id="evp-accept-all">Accept all (${actionable})</button>`
      : '';

    // Header: for a single event show its name + venue + magnitude badge;
    // for several, a count and the shared date.
    const single = evs.length === 1 ? evs[0] : null;
    const title = single ? Formatters.escapeHtml(single.name) : `${evs.length} events`;

    let subBits = `<span>${Formatters.escapeHtml(this.safeDate(date))}</span>`;
    let headBadge = '';
    if (single) {
      if (single.venue) subBits += `<span>·</span><span>${Formatters.escapeHtml(single.venue)}</span>`;
      headBadge = magPill(this.normMagnitude(single.magnitude));
    }

    return `
      <div class="evp-m-head">
        <div style="min-width:0;">
          <div class="evp-m-title">${title}</div>
          <div class="evp-m-sub">${subBits}${headBadge ? `<span style="margin-left:2px;">${headBadge}</span>` : ''}</div>
        </div>
        <div class="evp-m-actions">
          ${acceptAll}
          <button class="evp-m-x" id="evp-m-close" aria-label="Close" title="Close (Esc)">✕</button>
        </div>
      </div>
      <div class="evp-m-body">${sections}</div>`;
  }

  private renderModalEvent(ev: PricingEvent, date: string): string {
    const chip = magPill(this.normMagnitude(ev.magnitude));

    const nights = this.eventNights(ev).sort();
    const dateLabel = nights.length > 1
      ? `${this.safeDate(nights[0])} – ${this.safeDate(nights[nights.length - 1])}`
      : this.safeDate(ev.date);
    const venueLine = ev.venue ? ` · ${Formatters.escapeHtml(ev.venue)}` : '';
    const sourceTag = (ev.source && ev.source !== 'manual')
      ? `<span style="font-size:11px; color:var(--text-muted); margin-left:6px;">(${Formatters.escapeHtml(ev.source)})</span>`
      : '';

    const units = this.unitsOnNight(ev, date);

    const prompt = units.length
      ? callout(
          `💡 Demand is up near ${Formatters.escapeHtml(ev.venue || ev.name)} on ${Formatters.escapeHtml(this.safeDate(date))} — consider raising the rate for the ${units.length} nearby unit${units.length === 1 ? '' : 's'} below.`,
          'tip',
        )
      : '';

    const body = units.length
      ? `<table class="evp-table">
          <thead>
            <tr>
              <th>Unit</th>
              <th>Distance</th>
              <th>Current price</th>
              <th>Suggested price ${infoIcon('all_in_price')}</th>
              <th style="text-align:right;">Action</th>
            </tr>
          </thead>
          <tbody>
            ${units.map(u => this.renderUnitRow(ev, u, date)).join('')}
          </tbody>
        </table>`
      : `<div style="padding:8px 0;">${emptyState(
          'No nearby units',
          'None of your units are close enough to this event to suggest a price bump.',
          '📍',
        )}</div>`;

    return `
      <div class="evp-m-event">
        <div class="evp-m-event-head">
          <div style="min-width:0;">
            <div style="font-size:14px; font-weight:700; color:var(--text);">${Formatters.escapeHtml(ev.name)}${sourceTag}</div>
            <div style="font-size:12px; color:var(--text-muted); margin-top:2px;">${Formatters.escapeHtml(dateLabel)}${venueLine}</div>
          </div>
          <div style="display:flex; align-items:center; gap:8px;">
            ${chip}
            <button class="evp-del-btn" data-del-event="${Formatters.escapeHtml(ev.id)}">Delete</button>
          </div>
        </div>
        <div style="padding:14px 15px;">
          ${prompt}
          ${body}
        </div>
      </div>`;
  }

  private isActionable(_ev: PricingEvent, u: NearbyUnit, date: string): boolean {
    const rowKey = this.rowKey(u.listing_id, date);
    if (this.staged.has(rowKey)) return false;
    const current = u.current_price;
    const suggested = u.suggested_price;
    return Number.isFinite(suggested as number) && Number.isFinite(current as number) && (suggested as number) > (current as number);
  }

  private renderUnitRow(ev: PricingEvent, u: NearbyUnit, date: string): string {
    const rowKey = this.rowKey(u.listing_id, date);
    const isStaged = this.staged.has(rowKey);

    const current = Number.isFinite(u.current_price as number) ? (u.current_price as number) : null;
    const suggested = Number.isFinite(u.suggested_price as number) ? (u.suggested_price as number) : null;
    const upliftPct = this.computeUpliftPct(u);

    const distance = Number.isFinite(u.distance_mi as number)
      ? `${(u.distance_mi as number).toFixed(1)} mi`
      : '—';

    const upliftBadge = (upliftPct != null && upliftPct > 0)
      ? `<span class="evp-uplift">+${(upliftPct * 100).toFixed(0)}%</span>`
      : '';

    const canAccept = this.isActionable(ev, u, date);

    const btn = isStaged
      ? `<button class="evp-accept-btn" disabled>Staged ✓</button>`
      : `<button class="evp-accept-btn" data-accept-row="${Formatters.escapeHtml(rowKey)}"
            data-listing="${Formatters.escapeHtml(String(u.listing_id))}"
            data-date="${Formatters.escapeHtml(date)}"
            data-current="${current != null ? current : ''}"
            data-suggested="${suggested != null ? suggested : ''}"
            ${canAccept ? '' : 'disabled'}>Accept</button>`;

    return `
      <tr data-row-key="${Formatters.escapeHtml(rowKey)}" ${isStaged ? 'style="background:#f0fdf4;"' : ''}>
        <td style="font-weight:500;">${Formatters.escapeHtml(u.listing_name || String(u.listing_id))}</td>
        <td style="color:var(--text-muted);">${distance}</td>
        <td>${current != null ? Formatters.formatCurrency(current) : '—'}</td>
        <td style="font-weight:600;">${suggested != null ? Formatters.formatCurrency(suggested) : '—'}${upliftBadge}</td>
        <td style="text-align:right;">${btn}</td>
      </tr>`;
  }

  private bindModalEvents(): void {
    const card = document.getElementById('evp-modal-card');
    if (!card) return;

    card.querySelector('#evp-m-close')?.addEventListener('click', () => this.closeModal());

    card.querySelectorAll<HTMLButtonElement>('[data-del-event]').forEach(btn => {
      btn.addEventListener('click', () => this.deleteEvent(btn.dataset.delEvent || ''));
    });

    card.querySelectorAll<HTMLButtonElement>('[data-accept-row]').forEach(btn => {
      btn.addEventListener('click', () => this.acceptRow(btn));
    });

    const acceptAll = card.querySelector<HTMLButtonElement>('#evp-accept-all');
    if (acceptAll) acceptAll.addEventListener('click', () => this.acceptAll());
  }

  private async acceptAll(): Promise<void> {
    const card = document.getElementById('evp-modal-card');
    if (!card) return;
    const btns = Array.from(card.querySelectorAll<HTMLButtonElement>('[data-accept-row]'))
      .filter(b => !b.disabled);
    for (const btn of btns) {
      await this.acceptRow(btn);
    }
  }

  // ── Event handlers ─────────────────────────────────────────────────────────

  private bindForm(): void {
    const toggle = document.getElementById('evp-form-toggle');
    const wrap = document.getElementById('evp-form-wrap');
    const caret = document.getElementById('evp-form-caret');
    toggle?.addEventListener('click', () => {
      this.formOpen = !this.formOpen;
      if (wrap) wrap.style.display = this.formOpen ? 'block' : 'none';
      if (caret) caret.style.transform = this.formOpen ? 'rotate(90deg)' : 'none';
    });

    const form = document.getElementById('evp-form') as HTMLFormElement | null;
    const venueSelect = document.getElementById('evp-venue-select') as HTMLSelectElement | null;
    const venueInput = document.getElementById('evp-venue') as HTMLInputElement | null;
    const latInput = document.getElementById('evp-lat') as HTMLInputElement | null;
    const lngInput = document.getElementById('evp-lng') as HTMLInputElement | null;

    // Picking a known venue pre-fills its name + lat/lng.
    venueSelect?.addEventListener('change', () => {
      const idx = venueSelect.value;
      if (idx === '') return;
      const v = KNOWN_VENUES[Number(idx)];
      if (!v) return;
      if (venueInput) venueInput.value = v.name;
      if (latInput) latInput.value = String(v.lat);
      if (lngInput) lngInput.value = String(v.lng);
    });

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.submitEvent();
    });
  }

  private async submitEvent(): Promise<void> {
    const msg = document.getElementById('evp-form-msg');
    const addBtn = document.getElementById('evp-add-btn') as HTMLButtonElement | null;
    const setMsg = (t: string) => { if (msg) msg.textContent = t; };
    setMsg('');

    const name = (document.getElementById('evp-name') as HTMLInputElement)?.value.trim();
    const date = (document.getElementById('evp-date') as HTMLInputElement)?.value;
    const endDate = (document.getElementById('evp-end-date') as HTMLInputElement)?.value || null;
    const venue = (document.getElementById('evp-venue') as HTMLInputElement)?.value.trim();
    const magnitude = (document.getElementById('evp-magnitude') as HTMLSelectElement)?.value;
    const latRaw = (document.getElementById('evp-lat') as HTMLInputElement)?.value;
    const lngRaw = (document.getElementById('evp-lng') as HTMLInputElement)?.value;

    if (!name) { setMsg('Event name is required.'); return; }
    if (!date) { setMsg('A date is required.'); return; }

    const latitude = latRaw !== '' ? Number(latRaw) : null;
    const longitude = lngRaw !== '' ? Number(lngRaw) : null;
    if ((latitude != null && Number.isNaN(latitude)) || (longitude != null && Number.isNaN(longitude))) {
      setMsg('Latitude / longitude must be valid numbers.');
      return;
    }
    if (latitude == null || longitude == null) {
      setMsg('Pick a known venue or enter both latitude and longitude.');
      return;
    }

    if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Adding…'; }
    const result = await api.post<{ ok?: boolean }>('/events', {
      name, date, end_date: endDate, venue: venue || null, lat: latitude, lng: longitude, magnitude,
    });
    if (addBtn) { addBtn.disabled = false; addBtn.textContent = '+ Add event'; }

    if (!result) {
      setMsg('Could not add the event. Please try again.');
      return;
    }

    // Jump the calendar to the new event's month so it's visible.
    const m = date.match(/^(\d{4})-(\d{2})/);
    if (m) { this.viewYear = +m[1]; this.viewMonth = +m[2] - 1; }

    // Reset the form fields and reload.
    (document.getElementById('evp-form') as HTMLFormElement | null)?.reset();
    await this.loadData();
  }

  private async deleteEvent(id: string): Promise<void> {
    if (!id) return;
    const result = await api.post<{ ok?: boolean }>('/events/delete', { id });
    if (!result) {
      alert('Could not delete the event. Please try again.');
      return;
    }
    // Drop locally and re-render.
    this.events = this.events.filter(e => e.id !== id);

    // Refresh the modal if it's still showing this day; close if now empty.
    if (this.modalDate) {
      const dayMap = this.buildDayMap();
      const evs = dayMap.get(this.modalDate) || [];
      if (evs.length) {
        const card = document.getElementById('evp-modal-card');
        if (card) { card.innerHTML = this.renderModalContent(this.modalDate, evs); this.bindModalEvents(); }
      } else {
        this.closeModal();
      }
    }
    this.renderCalendar();
  }

  private async acceptRow(btn: HTMLButtonElement): Promise<void> {
    const rowKey = btn.dataset.acceptRow || '';
    const listingId = btn.dataset.listing || '';
    const stayDate = btn.dataset.date || '';
    const current = btn.dataset.current !== '' ? Number(btn.dataset.current) : null;
    const suggested = btn.dataset.suggested !== '' ? Number(btn.dataset.suggested) : null;
    if (!listingId || !stayDate || suggested == null) return;

    btn.disabled = true;
    btn.textContent = 'Staging…';

    const result = await api.post<{ ok?: boolean }>('/triage/actions', {
      listing_id: listingId,
      stay_date: stayDate,
      action_type: 'accept',
      target_price: suggested,
      current_price: current,
    });

    if (!result) {
      btn.disabled = false;
      btn.textContent = 'Accept';
      alert('Could not stage the price. Please try again.');
      return;
    }

    // Mark the row staged (persists across re-renders in this session).
    this.staged.add(rowKey);
    btn.textContent = 'Staged ✓';
    const row = document.querySelector<HTMLElement>(`tr[data-row-key="${CSS.escape(rowKey)}"]`);
    if (row) row.style.background = '#f0fdf4';

    // Refresh the "Accept all" count in the header to reflect the new total.
    const card = document.getElementById('evp-modal-card');
    const acceptAll = card?.querySelector<HTMLButtonElement>('#evp-accept-all');
    if (acceptAll) {
      const remaining = card!.querySelectorAll<HTMLButtonElement>('[data-accept-row]:not(:disabled)').length;
      if (remaining > 0) acceptAll.textContent = `Accept all (${remaining})`;
      else acceptAll.remove();
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private rowKey(listingId: string | number, date: string): string {
    return `${listingId}|${date}`;
  }

  // Date label that never renders "Invalid Date". Falls back to the raw string
  // (or an em-dash) when the value isn't a parseable YYYY-MM-DD — the shared
  // Formatters.formatDate would emit "Invalid Date" for malformed event dates.
  private safeDate(s: string | null | undefined): string {
    if (!s) return '—';
    const out = Formatters.formatDate(s);
    return /invalid/i.test(out) ? String(s) : out;
  }

  private normMagnitude(m: Magnitude | string | null | undefined): Magnitude | null {
    if (!m) return null;
    const v = String(m).toLowerCase();
    return (v === 'small' || v === 'medium' || v === 'large' || v === 'major') ? v : null;
  }

  // Resolve the uplift fraction (e.g. 0.18) from either an explicit field or
  // derived from current → suggested. Handles uplift given as a percent (18) too.
  private computeUpliftPct(u: NearbyUnit): number | null {
    if (u.uplift_pct != null && !Number.isNaN(u.uplift_pct)) {
      // Treat values >1 as already-a-percent (e.g. 18 → 0.18).
      return u.uplift_pct > 1 ? u.uplift_pct / 100 : u.uplift_pct;
    }
    if (Number.isFinite(u.current_price as number) && Number.isFinite(u.suggested_price as number) && (u.current_price as number) > 0) {
      return ((u.suggested_price as number) - (u.current_price as number)) / (u.current_price as number);
    }
    return null;
  }
}
